import { Permissions } from "@/type/api";

/**
 * Sensitive-contact (phone) visibility blocklists.
 *
 * Roles listed here are BLOCKED from seeing the corresponding phone numbers in
 * the UI. Full-access roles (Administrator / System Manager / Pet App Admin) are
 * never blocked. See `redactGuardianPhone` / `redactStaffPhone` / `redactCrmPhone`
 * and `canView*Phone` in `@/helper/permissionHelper`.
 *
 * ── DORMANT BY DESIGN ──────────────────────────────────────────────────────
 * All three lists ship EMPTY, so the feature is implemented but INACTIVE — no
 * number is hidden from anyone until a role is added below. "Implemented" and
 * "active" are different states here.
 *
 * ⚠️ BEFORE adding any role to GUARDIAN_PHONE_BLOCKED_ROLES: guardian `phone` is
 * also read for LOGIC (WhatsApp conversation matching / sending) and for
 * PERSISTENCE (case-sheet creation writes the guardian phone). Blanking it at the
 * mapper corrupts those flows. The un-redacted opt-in path (`phoneContact` split
 * + the ~12 logic-consumer rewire) MUST be built first — see docs/AGENT.md,
 * "Sensitive phone visibility", for the exact prerequisite and consumer list.
 *
 * The `Permissions` enum in `@/type/api` is the single source of truth for role
 * string literals. Example membership (do NOT enable without the review above):
 *   export const GUARDIAN_PHONE_BLOCKED_ROLES = [Permissions.Physician] as const;
 */

// Roles blocked from seeing GUARDIAN / pet-owner phone numbers.
export const GUARDIAN_PHONE_BLOCKED_ROLES: readonly Permissions[] = [
    Permissions.GuardianPhoneHidden,
];

// Roles blocked from seeing STAFF / user / driver / supplier phone numbers.
export const STAFF_PHONE_BLOCKED_ROLES: readonly Permissions[] = [
    Permissions.StaffPhoneHidden,
];

// Roles blocked from seeing CRM lead / customer phone numbers.
export const CRM_PHONE_BLOCKED_ROLES: readonly Permissions[] = [
    Permissions.CrmPhoneHidden,
];

export type SensitivePhoneCategory = "guardian" | "staff" | "crm";
